import { useCallback, useEffect } from 'react'
import { useQueryParams, StringParam } from 'use-query-params'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { useLocale } from '@percent/cause-dashboard/common/hooks/useLocale/useLocale'
import { useQuery } from '@percent/utility'

type UseCandidateTasksProps = {
  candidateId: string
  programId: string
}

export function useCandidateTasks({ candidateId, programId }: UseCandidateTasksProps) {
  const [queryParams, setQueryParams] = useQueryParams({ tasksView: StringParam, taskId: StringParam })
  const { grantsService } = useServices()
  const { locale } = useLocale()

  const {
    data: allTasks,
    isLoading,
    refetch
  } = useQuery(
    ['/tasks', candidateId, programId, locale],
    grantsService.getMyTasks,
    {
      candidateId: candidateId,
      locale
    },
    { enabled: !!candidateId && !!programId }
  )

  const tasksData = allTasks?.filter(task => task.status !== 'cancelled')

  useEffect(() => {
    // Open the tasks view if there are any tasks and at least one is todo only on mount
    if (!isLoading && tasksData?.some(task => task.status === 'todo')) {
      setQueryParams({ tasksView: 'true' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  const closeTasksView = useCallback(() => {
    setQueryParams({ tasksView: undefined, taskId: undefined })
  }, [setQueryParams])

  const openTasksView = () => {
    setQueryParams({
      tasksView: 'true'
    })
  }

  return {
    tasks: tasksData || [],
    isTasksViewOpen: queryParams.tasksView === 'true',
    closeTasksView,
    openTasksView,
    refetchTasks: refetch
  }
}
